import { useState, useEffect, useRef } from "react";

const LoadingView = ({ progress }) => {
  const [yards, setYards] = useState(100);
  const [swingPhase, setSwingPhase] = useState(0);
  const [ballFlying, setBallFlying] = useState(false);
  const [ballX, setBallX] = useState(0);
  const [ballY, setBallY] = useState(0);
  const [ballSize, setBallSize] = useState(8);
  const [buggyProgress, setBuggyProgress] = useState(0);
  const maxProgressRef = useRef(0);

  useEffect(() => {
    const newYards = Math.max(0, Math.round(100 - progress));
    setYards(newYards);
    
    // Only move buggy forward, never backward
    if (progress > maxProgressRef.current) {
      maxProgressRef.current = progress;
      setBuggyProgress(progress);
    }
  }, [progress]);

  // Swing animation sequence - plays once
  useEffect(() => {
    const sequence = [
      { phase: 1, delay: 400 },   // backswing
      { phase: 2, delay: 600 },   // top
      { phase: 3, delay: 200 },   // downswing
      { phase: 4, delay: 100 },   // impact
      { phase: 5, delay: 50 },    // follow through
    ];

    let timeouts = [];
    let cumulative = 500;

    sequence.forEach(({ phase, delay }) => {
      cumulative += delay;
      const t = setTimeout(() => setSwingPhase(phase), cumulative);
      timeouts.push(t);
    });

    // Start ball animation after impact
    const ballStart = setTimeout(() => {
      setBallFlying(true);
    }, cumulative - 100);
    timeouts.push(ballStart);

    return () => timeouts.forEach(clearTimeout);
  }, []);

  // Ball flight animation
  useEffect(() => {
    if (!ballFlying) return;
    
    let frame = 0;
    const interval = setInterval(() => {
      frame++;
      setBallX(frame * 4);
      // Parabolic arc
      const t = frame / 50;
      setBallY(-120 * t + 120 * t * t);
      setBallSize(Math.max(2, 8 - frame * 0.1));
      
      if (frame > 80) {
        clearInterval(interval);
      }
    }, 25);

    return () => clearInterval(interval);
  }, [ballFlying]);

  return (
    <div className="loading-screen" data-testid="loading-view">
      {/* Background */}
      <div className="absolute inset-0 bg-[#0A0A0A]" />

      {/* Header - matching dashboard */}
      <header className="absolute top-0 left-0 right-0 border-b border-[#262626] px-6 md:px-12 py-6 z-20">
        <div className="flex items-center gap-4">
          <img 
            src="https://rezzil.com/wp-content/uploads/2023/11/Rezzil-Logo-White-800.png" 
            alt="Rezzil" 
            className="h-8"
          />
          <span className="font-heading text-xl tracking-wider text-[#A3A3A3] uppercase">
            Pro-Shot
          </span>
        </div>
      </header>

      {/* Main Content */}
      <div className="relative z-10 w-full max-w-3xl px-6 text-center">
        
        {/* Golfer Animation - Minimalist Style */}
        <div className="mb-6 h-64 relative overflow-hidden">
          <svg viewBox="0 0 400 220" className="w-full h-full">
            {/* Background - white line art */}
            {/* Ground line */}
            <line x1="0" y1="170" x2="400" y2="170" stroke="#333" strokeWidth="1" />
            
            {/* Distant hills - white lines */}
            <path d="M0 170 Q100 140 200 170 Q300 150 400 170" fill="none" stroke="#222" strokeWidth="1" />
            
            {/* Trees - white line art */}
            <g stroke="#444" strokeWidth="1" fill="none">
              <path d="M300 170 L300 140 M290 155 L300 145 L310 155" />
              <path d="M330 170 L330 130 M318 150 L330 138 L342 150 M320 160 L330 150 L340 160" />
              <path d="M370 170 L370 145 M362 158 L370 148 L378 158" />
            </g>

            {/* Flag - white pole, green flag */}
            <g transform="translate(350, 110)">
              <line x1="0" y1="0" x2="0" y2="60" stroke="#fff" strokeWidth="1.5" />
              <path d="M0 0 L18 7 L0 14 Z" fill="#CCFF00" />
              {/* Hole */}
              <ellipse cx="0" cy="60" rx="8" ry="2" fill="none" stroke="#fff" strokeWidth="1" />
            </g>
            
            {/* Golfer - solid green silhouette */}
            <g transform="translate(120, 40)">
              {swingPhase === 0 && <GolferAddress />}
              {swingPhase === 1 && <GolferBackswing />}
              {swingPhase === 2 && <GolferTop />}
              {swingPhase === 3 && <GolferDownswing />}
              {swingPhase === 4 && <GolferImpact />}
              {swingPhase === 5 && <GolferFollowThrough />}
            </g>
            
            {/* Tee - white line */}
            <line x1="153" y1="170" x2="157" y2="178" stroke="#fff" strokeWidth="1" />
            
            {/* Golf Ball - GREEN */}
            {!ballFlying && swingPhase < 4 && (
              <circle cx="155" cy="168" r="5" fill="#CCFF00" />
            )}
            
            {/* Ball in flight - GREEN with trail */}
            {ballFlying && ballX < 300 && (
              <g>
                {/* Trail dots */}
                <circle cx={155 + ballX * 0.6} cy={168 + ballY * 0.5} r="2" fill="#CCFF00" opacity="0.15" />
                <circle cx={155 + ballX * 0.7} cy={168 + ballY * 0.6} r="2.5" fill="#CCFF00" opacity="0.25" />
                <circle cx={155 + ballX * 0.85} cy={168 + ballY * 0.85} r="3" fill="#CCFF00" opacity="0.4" />
                {/* Ball */}
                <circle cx={155 + ballX} cy={168 + ballY} r={ballSize} fill="#CCFF00" />
              </g>
            )}
          </svg>
        </div>

        {/* Analyzing Text */}
        <h2 className="font-heading font-bold text-2xl md:text-3xl uppercase tracking-tight text-white mb-1">
          Analyzing Your Swing
        </h2>
        <p className="text-[#A3A3A3] text-sm mb-8">
          Processing video frame by frame
        </p>

        {/* Golf Cart Track */}
        <div className="relative h-16 mb-6 overflow-hidden">
          {/* Path */}
          <div className="absolute left-4 right-4 top-1/2 h-1 bg-[#1a1a1a] rounded" />
          
          {/* Golf Buggy - facing right */}
          <div 
            className="absolute top-1/2"
            style={{ 
              left: `${Math.min(buggyProgress, 88)}%`,
              transform: 'translateY(-50%) scaleX(-1)',
              transition: 'left 1s linear',
              willChange: 'left'
            }}
          >
            <svg width="48" height="40" viewBox="0 0 100 80" fill="#CCFF00">
              {/* Roof */}
              <path d="M20 5 L20 20 L75 20 L75 5 L20 5 Z" />
              {/* Roof support left */}
              <path d="M25 20 L15 45 L22 45 L30 20 Z" />
              {/* Roof support right */}
              <path d="M70 20 L70 35 L77 35 L77 20 Z" />
              {/* Body */}
              <path d="M8 45 L8 60 L85 60 L92 45 L75 45 L75 35 L70 35 L70 45 L30 45 Z" />
              {/* Back section */}
              <path d="M75 35 L75 60 L92 60 L92 45 L85 35 Z" />
              {/* Steering wheel */}
              <rect x="45" y="38" width="12" height="3" fill="#0A0A0A" />
              <rect x="50" y="32" width="2" height="8" fill="#0A0A0A" />
              {/* Front wheel */}
              <circle cx="25" cy="65" r="12" fill="#0A0A0A" />
              <circle cx="25" cy="65" r="6" fill="#1a1a1a" />
              {/* Back wheel */}
              <circle cx="75" cy="65" r="12" fill="#0A0A0A" />
              <circle cx="75" cy="65" r="6" fill="#1a1a1a" />
            </svg>
          </div>

          {/* Flag at end */}
          <div className="absolute right-2 top-1/2 -translate-y-1/2">
            <svg width="24" height="32" viewBox="0 0 24 24" fill="none" stroke="#CCFF00" strokeWidth="2">
              <path d="M4 22V4" />
              <path d="M4 4l12 4-12 4" fill="#CCFF00" />
            </svg>
          </div>
        </div>

        {/* Yardage Counter */}
        <div data-testid="yardage-display">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#A3A3A3] mb-2">
            Distance to Hole
          </p>
          <div className="font-heading font-black text-5xl text-[#CCFF00] tracking-tight">
            {yards}
            <span className="text-xl ml-2 text-[#A3A3A3] font-normal">YARDS</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Golfer pose components - realistic proportions
const GolferAddress = () => (
  <g fill="none" stroke="#CCFF00" strokeWidth="3" strokeLinecap="round">
    {/* Head */}
    <circle cx="30" cy="20" r="10" fill="#CCFF00" />
    {/* Cap brim */}
    <path d="M20 18 L40 18" strokeWidth="4" />
    
    {/* Torso - bent at address */}
    <path d="M30 30 L35 70" />
    
    {/* Lead leg */}
    <path d="M35 70 L45 110 L50 130" />
    {/* Trail leg */}
    <path d="M35 70 L25 110 L20 130" />
    
    {/* Arms */}
    <path d="M32 40 L45 55 L35 90" />
    
    {/* Club */}
    <path d="M35 90 L38 125" stroke="#CCFF00" strokeWidth="2" />
    <rect x="35" y="123" width="8" height="4" fill="#CCFF00" rx="1" />
  </g>
);

const GolferBackswing = () => (
  <g fill="none" stroke="#CCFF00" strokeWidth="3" strokeLinecap="round">
    <circle cx="30" cy="22" r="10" fill="#CCFF00" />
    <path d="M20 20 L40 20" strokeWidth="4" />
    
    <path d="M30 32 L38 70" />
    <path d="M38 70 L48 110 L52 130" />
    <path d="M38 70 L25 110 L18 130" />
    
    {/* Arms going back */}
    <path d="M32 42 L15 50 L-5 35" />
    
    {/* Club going back */}
    <path d="M-5 35 L-25 10" strokeWidth="2" />
    <rect x="-30" y="5" width="8" height="4" fill="#CCFF00" rx="1" transform="rotate(-60 -26 7)" />
  </g>
);

const GolferTop = () => (
  <g fill="none" stroke="#CCFF00" strokeWidth="3" strokeLinecap="round">
    <circle cx="28" cy="25" r="10" fill="#CCFF00" />
    <path d="M18 23 L38 23" strokeWidth="4" />
    
    <path d="M28 35 L40 72" />
    <path d="M40 72 L50 112 L54 130" />
    <path d="M40 72 L28 112 L22 130" />
    
    {/* Arms at top */}
    <path d="M30 42 L10 40 L-5 20" />
    
    {/* Club at top - parallel to ground */}
    <path d="M-5 20 L20 5" strokeWidth="2" />
    <rect x="18" y="0" width="8" height="4" fill="#CCFF00" rx="1" transform="rotate(20 22 2)" />
  </g>
);

const GolferDownswing = () => (
  <g fill="none" stroke="#CCFF00" strokeWidth="3" strokeLinecap="round">
    <circle cx="30" cy="22" r="10" fill="#CCFF00" />
    <path d="M20 20 L40 20" strokeWidth="4" />
    
    <path d="M30 32 L38 70" />
    <path d="M38 70 L48 110 L52 130" />
    <path d="M38 70 L28 110 L22 130" />
    
    {/* Arms coming down */}
    <path d="M32 42 L40 60 L30 95" />
    
    {/* Club coming down */}
    <path d="M30 95 L35 125" strokeWidth="2" />
    <rect x="32" y="123" width="8" height="4" fill="#CCFF00" rx="1" />
  </g>
);

const GolferImpact = () => (
  <g fill="none" stroke="#CCFF00" strokeWidth="3" strokeLinecap="round">
    <circle cx="32" cy="20" r="10" fill="#CCFF00" />
    <path d="M22 18 L42 18" strokeWidth="4" />
    
    <path d="M32 30 L38 68" />
    <path d="M38 68 L50 108 L55 130" />
    <path d="M38 68 L28 108 L22 130" />
    
    {/* Arms at impact - extended */}
    <path d="M34 40 L50 55 L38 100" />
    
    {/* Club at impact */}
    <path d="M38 100 L38 132" strokeWidth="2" />
    <rect x="35" y="130" width="8" height="4" fill="#CCFF00" rx="1" />
    
    {/* Impact burst */}
    <circle cx="39" cy="132" r="8" fill="none" stroke="#fff" strokeWidth="1" opacity="0.5" />
  </g>
);

const GolferFollowThrough = () => (
  <g fill="none" stroke="#CCFF00" strokeWidth="3" strokeLinecap="round">
    <circle cx="38" cy="22" r="10" fill="#CCFF00" />
    <path d="M28 20 L48 20" strokeWidth="4" />
    
    <path d="M38 32 L42 68" />
    <path d="M42 68 L55 105 L60 130" />
    <path d="M42 68 L35 108 L32 130" />
    
    {/* Arms in follow through - high finish */}
    <path d="M40 40 L65 30 L80 10" />
    
    {/* Club in follow through */}
    <path d="M80 10 L95 -15" strokeWidth="2" />
    <rect x="93" y="-20" width="8" height="4" fill="#CCFF00" rx="1" transform="rotate(50 97 -18)" />
  </g>
);

export default LoadingView;
